import { Card } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ALL_WEEKDAYS, ALL_WEEKEND_DAYS } from "@src/appV2/lib/dates/types";
import { shiftTypeOptions } from "@src/appV2/redesign/PlacementCandidate/constants";
import { type JobType, type ShiftNameType } from "@src/appV2/redesign/PlacementCandidate/types";
import { augmentYearsOfExperienceWithWorkerLicenses } from "@src/appV2/redesign/PlacementCandidate/utils";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { type BenefitsDifferential, findMatchingPayBreakdown } from "../../lib/estimatedPay";
import {
  getYearsOfExperienceOrFallback,
  type PlacementForComputedPay,
} from "../../lib/usePayTransparencyRates";
import { type TransparencyPayRate } from "../../types/payRate";
import { type PayAndScheduleFormData } from "../PayAndScheduleForm/usePayAndScheduleForm";
import { ShiftDaysDisplay } from "./ShiftDaysDisplay";
import { ShiftPayRatesDisplay } from "./ShiftPayRatesDisplay";
import { ShiftTimesDisplay } from "./ShiftTimesDisplay";
import { ShiftTypeChip } from "./ShiftTypeChip";
import { getShiftTimesForType } from "./utils/getShiftTimesForType";

interface PayTableProps {
  placement: PlacementForComputedPay;
  transparencyPayRates: TransparencyPayRate[];
  highestMatchingQualification: string;
  selectedJobType: JobType;
  benefitsDifferential: BenefitsDifferential;
  placementCandidateId: string;
  workerId: string;
}

export function PayTableV2(props: PayTableProps) {
  const {
    placement,
    transparencyPayRates,
    highestMatchingQualification,
    selectedJobType,
    benefitsDifferential,
    placementCandidateId,
    workerId,
  } = props;

  const worker = useDefinedWorker();

  const { watch } = useFormContext<PayAndScheduleFormData>();

  const enteredYearsOfExperienceByQualification = watch("yearsOfExperienceByQualification");

  const yearsOfExperienceByQualification = augmentYearsOfExperienceWithWorkerLicenses(
    enteredYearsOfExperienceByQualification,
    worker.licenses
  );

  const yearsOfExperience = getYearsOfExperienceOrFallback(
    yearsOfExperienceByQualification?.[highestMatchingQualification]
  );

  const isShiftTypeOffered = (jobType: JobType, shiftType: ShiftNameType) => {
    return (
      placement?.jobShifts.some(
        (jobShift) => jobShift.jobType === jobType && jobShift.shiftTypes.includes(shiftType)
      ) ?? false
    );
  };

  const isWeekdayOffered = (jobType: JobType, shiftType: ShiftNameType) => {
    return (
      placement?.jobShifts.some(
        (jobShift) =>
          jobShift.jobType === jobType &&
          jobShift.shiftTypes.includes(shiftType) &&
          ALL_WEEKDAYS.some((dayOfWeek) => jobShift.daysOfWeek.includes(dayOfWeek))
      ) ?? false
    );
  };

  const isWeekendOffered = (jobType: JobType, shiftType: ShiftNameType) => {
    return (
      placement?.jobShifts.some(
        (jobShift) =>
          jobShift.jobType === jobType &&
          jobShift.shiftTypes.includes(shiftType) &&
          ALL_WEEKEND_DAYS.some((dayOfWeek) => jobShift.daysOfWeek.includes(dayOfWeek))
      ) ?? false
    );
  };

  const shiftsUiData = shiftTypeOptions.map((shiftTypeOption) => {
    const isOffered = isShiftTypeOffered(selectedJobType, shiftTypeOption.value);

    const weekdayOffered = isWeekdayOffered(selectedJobType, shiftTypeOption.value);
    const weekdayPayRate = findMatchingPayBreakdown({
      payRates: transparencyPayRates,
      jobType: selectedJobType,
      shiftType: shiftTypeOption.value,
      daysOfWeek: ALL_WEEKDAYS,
      benefits: benefitsDifferential,
      yearsOfExperience,
    });

    const weekendOffered = isWeekendOffered(selectedJobType, shiftTypeOption.value);
    const weekendPayRate = findMatchingPayBreakdown({
      payRates: transparencyPayRates,
      jobType: selectedJobType,
      shiftType: shiftTypeOption.value,
      daysOfWeek: ALL_WEEKEND_DAYS,
      benefits: benefitsDifferential,
      yearsOfExperience,
    });

    const isWeekendAndWeekdayPayRateSame =
      weekdayOffered &&
      weekendOffered &&
      isDefined(weekdayPayRate) &&
      isDefined(weekendPayRate) &&
      weekdayPayRate.overallPay === weekendPayRate.overallPay;

    const shiftTimes = getShiftTimesForType(placement, selectedJobType, shiftTypeOption.value);

    return {
      shiftTypeOption,
      isOffered,
      weekdayOffered,
      weekdayPayRate,
      weekendOffered,
      weekendPayRate,
      isWeekendAndWeekdayPayRateSame,
      shiftTimes,
    };
  });
  const filteredShiftsUiData = shiftsUiData.filter(
    (shift) => isDefined(shift.weekdayPayRate) || isDefined(shift.weekendPayRate)
  );

  const shiftsUiDataForAnalytics = shiftsUiData.reduce<
    Partial<
      Record<
        ShiftNameType,
        {
          shiftType: ShiftNameType;
          isOffered: boolean;
          weekdayOffered: boolean;
          weekendOffered: boolean;
          weekdayPayRate: number | undefined;
          weekendPayRate: number | undefined;
        }
      >
    >
  >(
    (
      accumulator,
      { shiftTypeOption, isOffered, weekdayOffered, weekendOffered, weekdayPayRate, weekendPayRate }
    ) => {
      accumulator[shiftTypeOption.value] = {
        shiftType: shiftTypeOption.value,
        isOffered,
        weekdayOffered,
        weekendOffered,
        weekdayPayRate: weekdayPayRate?.overallPay,
        weekendPayRate: weekendPayRate?.overallPay,
      };
      return accumulator;
    },
    {}
  );

  const analyticsPayload = {
    placementId: placement?.id,
    placementCandidateId,
    workerId,
    workplaceId: placement?.workplaceId,
    jobType: selectedJobType,
    yearsOfExperience: enteredYearsOfExperienceByQualification?.[highestMatchingQualification],
    yearsOfExperienceAugmented: yearsOfExperienceByQualification?.[highestMatchingQualification],
    highestMatchingQualification,
    useCombinedBenefit: benefitsDifferential.combined,
    useHealthInsuranceBenefit: benefitsDifferential.health,
    usePaidTimeOffBenefit: benefitsDifferential.pto,
    ...shiftsUiDataForAnalytics,
  };

  const analyticsPayloadSer = JSON.stringify(analyticsPayload);

  useEffect(() => {
    const analyticsPayloadDeser = JSON.parse(analyticsPayloadSer) as typeof analyticsPayload;
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAY_AND_SCHEDULE_FORM_VIEWED, analyticsPayloadDeser);
  }, [analyticsPayloadSer]);

  return (
    <Card elevation={3} sx={{ px: 7, py: 4 }}>
      <Stack>
        {filteredShiftsUiData.map(
          (
            {
              shiftTypeOption,
              isOffered,
              weekdayOffered,
              weekdayPayRate,
              weekendOffered,
              weekendPayRate,
              isWeekendAndWeekdayPayRateSame,
              shiftTimes,
            },
            index
          ) => (
            <Stack
              key={shiftTypeOption.value}
              direction="row"
              alignItems="flex-start"
              borderBottom={(theme) =>
                index < filteredShiftsUiData.length - 1
                  ? `1px dashed ${theme.palette.border?.subtle}`
                  : "none"
              }
              py={6}
            >
              <Box flex="1 1 0%" overflow="hidden">
                <Stack spacing={2} alignItems="flex-start">
                  <ShiftTypeChip shiftType={shiftTypeOption.value} />
                  {shiftTimes.length > 0 && (
                    <ShiftTimesDisplay shiftType={shiftTypeOption.value} shiftTimes={shiftTimes} />
                  )}
                </Stack>
              </Box>
              <ShiftDaysDisplay
                isOffered={isOffered}
                weekdayOffered={weekdayOffered}
                weekdayPayRate={weekdayPayRate}
                weekendOffered={weekendOffered}
                weekendPayRate={weekendPayRate}
                isWeekendAndWeekdayPayRateSame={isWeekendAndWeekdayPayRateSame}
                shiftTypeLabel={shiftTypeOption.label}
                sx={{ mt: shiftTimes.length > 0 ? 7 : 0 }}
              />
              <ShiftPayRatesDisplay
                isOffered={isOffered}
                weekdayOffered={weekdayOffered}
                weekdayPayRate={weekdayPayRate}
                weekendOffered={weekendOffered}
                weekendPayRate={weekendPayRate}
                isWeekendAndWeekdayPayRateSame={isWeekendAndWeekdayPayRateSame}
                shiftTypeLabel={shiftTypeOption.label}
                sx={{ mt: shiftTimes.length > 0 ? 7 : 0 }}
              />
            </Stack>
          )
        )}
      </Stack>
    </Card>
  );
}
